import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";

const Contact = () => {
  const formRef = useRef();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    const state = location.state;

    if (state?.source === "product") {
      setFormData((prev) => ({
        ...prev,
        subject: `Product Demo Request - ${state.productTitle}`,
        message: `Hello CBS Team,

I'm interested in requesting a demo for your "${state.productTitle}" product.

I would like to learn more about:
• Features and functionality
• Pricing and implementation
• Customization options
• Integration capabilities

Please contact me with more details.

Thank you.`,
      }));
    }

    if (state?.source === "service") {
      setFormData((prev) => ({
        ...prev,
        subject: `Service Inquiry - ${state.serviceTitle}`,
        message: `Hello CBS Team,

I'm interested in your "${state.serviceTitle}" service.

I would like to know more about:
• Service scope
• Development process
• Estimated timeline
• Pricing and support

Please reach out with more information.

Thank you.`,
      }));
    }

    // Scroll to form
    setTimeout(() => {
      const element = document.getElementById("contact-form");

      if (element) {
        const navbarHeight = 150;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - navbarHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }, 100);
  }, [location.state]);

  const sendMail = async (e) => {
    e.preventDefault();

    setLoading(true);
    setSuccess("");
    setError("");

    try {
      const res = await fetch(
        "https://contact-us-mail-api.connected.com.mm/api/v1/messages/sendMail",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-website-id": "69f9a919ac475d2b778d547d",
          },
          body: JSON.stringify({
            name: formData.fullName,
            email: formData.email,
            phone: formData.phone,
            subject: formData.subject,
            body: formData.message,
          }),
        },
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to send");
      }

      setSuccess("Message sent successfully!");
      setFormData({
        fullName: "",
        phone: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (err) {
      setError(err.message || "Something went wrong");
    }

    setLoading(false);
  };

  return (
    <div className="bg-[#0B0F19] text-white min-h-screen font-sans selection:bg-cyan-500">
      <div className="max-w-[1400px] mx-auto text-center pt-24 md:pt-32 px-4 md:px-6">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-4xl sm:text-5xl md:text-7xl font-black uppercase italic tracking-tighter">
          Contact <span className="text-cyan-400">Us</span>
        </motion.h2>

        <p className="w-full md:w-[50%] mx-auto text-[#A0AEC0] mt-4 md:mt-6 text-base md:text-lg leading-relaxed px-2">
          We strive to respond to all inquiries as quickly as possible. Your
          questions and feedback bridge the gap between our technology and your
          success.
        </p>

        <div className="lg:flex items-start mx-auto gap-8 md:gap-12 mt-12 md:mt-20 mb-12 md:mb-12 px-2 md:px-4">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="lg:flex-1 bg-[#111827] border border-gray-800 p-3 md:p-4 lg:p-6 rounded-[20px] md:rounded-[40px] shadow-2xl">
            <form
              id="contact-form"
              ref={formRef}
              onSubmit={sendMail}
              className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 text-left">
              <div className="space-y-1.5 md:space-y-2">
                <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-cyan-400">
                  Full Name
                </span>
                <input
                  name="fullName"
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) =>
                    setFormData({ ...formData, fullName: e.target.value })
                  }
                  placeholder="John Doe"
                  className="w-full rounded-xl md:rounded-2xl bg-white/5 border border-gray-700 h-[40px] md:h-[45px] px-3 md:px-4 text-sm md:text-base focus:border-cyan-500 outline-none transition-all"
                />
              </div>

              <div className="space-y-1.5 md:space-y-2">
                <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-cyan-400">
                  Phone
                </span>
                <input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  placeholder="09..."
                  className="w-full rounded-xl md:rounded-2xl bg-white/5 border border-gray-700 h-[40px] md:h-[45px] px-3 md:px-4 text-sm md:text-base focus:border-cyan-500 outline-none transition-all"
                />
              </div>

              <div className="space-y-1.5 md:space-y-2">
                <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-cyan-400">
                  Email
                </span>
                <input
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="john@example.com"
                  className="w-full rounded-xl md:rounded-2xl bg-white/5 border border-gray-700 h-[40px] md:h-[45px] px-3 md:px-4 text-sm md:text-base focus:border-cyan-500 outline-none transition-all"
                />
              </div>

              <div className="md:col-span-2 space-y-1.5 md:space-y-2">
                <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-cyan-400">
                  Subject
                </span>
                <input
                  name="subject"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) =>
                    setFormData({ ...formData, subject: e.target.value })
                  }
                  placeholder="What is this about?"
                  className="w-full rounded-xl md:rounded-2xl bg-white/5 border border-gray-700 h-[40px] md:h-[45px] px-3 md:px-4 text-sm md:text-base focus:border-cyan-500 outline-none transition-all"
                />
              </div>

              <div className="md:col-span-2 space-y-1.5 md:space-y-2">
                <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-cyan-400">
                  Message
                </span>
                <textarea
                  name="message"
                  rows="14"
                  required
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  placeholder="How can we help?"
                  className="w-full rounded-xl md:rounded-2xl bg-white/5 border border-gray-700 p-3 md:p-4 text-sm md:text-base focus:border-cyan-500 outline-none transition-all resize-none"></textarea>
              </div>

              <div className="md:col-span-2 flex items-center gap-2 md:gap-3 py-2 md:py-3">
                <input
                  type="checkbox"
                  id="terms"
                  required
                  className="w-4 h-4 md:w-5 md:h-5 rounded border-gray-700 bg-gray-900 text-cyan-500 focus:ring-cyan-500"
                />
                <label
                  htmlFor="terms"
                  className="text-[10px] md:text-xs text-gray-400 font-medium">
                  I agree with terms of usage and privacy policy.
                </label>
              </div>

              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full md:w-auto px-8 md:px-12 py-3 md:py-4 rounded-full font-black uppercase tracking-tighter text-white text-sm md:text-base transition-transform hover:scale-105 disabled:opacity-50"
                  style={{
                    background:
                      "linear-gradient(100deg, #007BFF 0%, #05DFAD 100%)",
                  }}>
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </div>

              {success && (
                <p className="md:col-span-2 text-lg md:text-xl text-green-400 font-semibold">
                  {success}
                </p>
              )}

              {error && (
                <p className="md:col-span-2 text-sm md:text-base text-red-400 font-semibold">
                  {error}
                </p>
              )}
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="lg:w-[300px] md:w-[350px] flex flex-col gap-4 md:gap-6 mt-8 lg:mt-0">
            {[
              {
                label: "Sales",
                email: "sales@cbs.com.mm",
                icon: "/photos/Icon7.png",
              },
              {
                label: "Partners",
                email: "partner@cbs.com.mm",
                icon: "/photos/Icon8.png",
              },
              {
                label: "Support",
                email: "support@cbs.com.mm",
                icon: "/photos/Icon9.png",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-white/5 border border-gray-800 p-6 md:p-8 rounded-2xl md:rounded-3xl text-center hover:border-cyan-500/50 transition-all">
                <img
                  src={item.icon}
                  alt={item.label}
                  className="w-8 h-8 md:w-10 md:h-10 mx-auto mb-3 md:mb-4"
                />
                <h4 className="font-bold text-white uppercase text-xs md:text-sm tracking-widest">
                  {item.label}
                </h4>
                <p className="text-cyan-400 text-xs md:text-sm mt-1">
                  {item.email}
                </p>
              </div>
            ))}

            <img
              src="/photos/Box.png"
              className="rounded-2xl md:rounded-3xl object-cover opacity-60 hover:opacity-100 transition-opacity w-full"
              alt="Decoration"
            />
          </motion.div>
        </div>
      </div>
      <div className="py-16 md:py-24 bg-[#0F172A]/50 px-4 md:px-6">
        <div className="max-w-4xl mx-auto text-center mb-8 md:mb-12">
          <span className="bg-cyan-500/20 text-cyan-400 px-4 md:px-6 py-2 rounded-full font-black text-xs uppercase tracking-widest">
            Our Headquarters
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-white mt-4 md:mt-6 uppercase italic">
            Here to Find Us
          </h2>
          <p className="text-gray-400 mt-2 md:mt-4 text-sm md:text-base px-2">
            Visit our offices to discuss your digital future in person.
          </p>
        </div>
        <div className="max-w-6xl mx-auto rounded-[20px] md:rounded-[40px] overflow-hidden border border-gray-800 shadow-2xl">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3818.1720588592443!2d96.12701707289827!3d16.86738108209172!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30c1950c4161e84f%3A0x94f7ead79a434d01!2sConnected%20Business%20Solutions!5e0!3m2!1sen!2smy!4v1707554170190!5m2!1sen!2smy"
            width="100%"
            height="350px"
            style={{ border: 0, filter: "invert(90%) hue-rotate(180deg)" }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="opacity-80"></iframe>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <div className="relative p-8 md:p-12 lg:p-20 rounded-[2rem] md:rounded-[3rem] overflow-hidden text-center bg-gradient-to-br from-blue-600 to-cyan-500">
          <div className="relative z-10">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-white uppercase italic leading-none mb-4 md:mb-6">
              Our Dedicated Team is Here <br /> to provide you
            </h2>
            <p className="text-white/80 font-medium mb-6 md:mb-10 text-sm md:text-base">
              Explore the features and possibilities our solutions offer.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
